import { z } from 'zod'
import { Bool } from './bool'

/**
 * @summary Reusable schema for STARTDATE field representing the start date of record validity.
 *
 * @description This schema should be used in all GAR item schemas that require a STARTDATE field.
 * The `describe()` method is called here to provide a common description.
 * Use `.optional()` only when applying this schema in specific contexts where the field is optional.
 *
 * @example
 * ```ts
 * const parsedDate = StartDate.parse('2023-01-01');
 * ```
 */
export const StartDate = z.coerce
  .date<string>()
  .describe('Start date of record validity')

/**
 * @summary Reusable schema for ENDDATE field representing the end date of record validity.
 *
 * @description This schema should be used in all GAR item schemas that require an ENDDATE field.
 * The `describe()` method is called here to provide a common description.
 * Use `.optional()` only when applying this schema in specific contexts where the field is optional.
 *
 * @example
 * ```ts
 * const parsedDate = EndDate.parse('2023-01-01');
 * ```
 */
export const EndDate = z.coerce
  .date<string>()
  .describe('End date of record validity')

/**
 * @summary Reusable schema for UPDATEDATE field representing the record update date.
 *
 * @description This schema should be used in all GAR item schemas that require an UPDATEDATE field.
 * The `describe()` method is called here to provide a common description.
 * Use `.optional()` only when applying this schema in specific contexts where the field is optional.
 *
 * @example
 * ```ts
 * const parsedDate = UpdateDate.parse('2023-01-01');
 * ```
 */
export const UpdateDate = z.coerce
  .date<string>()
  .describe('Date of entry (update) of the record')

/**
 * @summary Reusable schema for ISACTIVE field representing the activity status.
 *
 * @description This schema should be used in all GAR item schemas that require an ISACTIVE field.
 * The `describe()` method is called here to provide a common description.
 * Use `.optional()` only when applying this schema in specific contexts where the field is optional.
 *
 * @example
 * ```ts
 * const parsedValue = IsActive.parse('true');
 * ```
 */
export const IsActive = Bool().describe('Activity status')

/**
 * @summary Reusable schema for ISACTUAL field representing the address object relevance status.
 *
 * @description This schema should be used in all GAR item schemas that require an ISACTUAL field.
 * The `describe()` method is called here to provide a common description.
 * Use `.optional()` only when applying this schema in specific contexts where the field is optional.
 *
 * @example
 * ```ts
 * const parsedValue = IsActual.parse('true');
 * ```
 */
export const IsActual = Bool().describe('Address object relevance status')

/**
 * @summary Reusable schema for OBJECTID field representing the global unique identifier of an object.
 *
 * @description This schema should be used in all GAR item schemas that require an OBJECTID field.
 * The `describe()` method is called here to provide a common description.
 * Use `.optional()` only when applying this schema in specific contexts where the field is optional.
 *
 * @example
 * ```ts
 * const parsedId = ObjectId.parse('12345');
 * ```
 */
export const ObjectId = z.coerce
  .number<string>()
  .int()
  .describe('Global unique identifier of the object (integer)')

/**
 * @summary Reusable schema for OBJECTGUID field representing the global unique identifier of an object (UUID format).
 *
 * @description This schema should be used in all GAR item schemas that require an OBJECTGUID field.
 * The `describe()` method is called here to provide a common description.
 * Use `.optional()` only when applying this schema in specific contexts where the field is optional.
 *
 * @example
 * ```ts
 * const parsedGuid = ObjectGuid.parse('550e8400-e29b-41d4-a716-446655440000');
 * ```
 */
export const ObjectUuid = z
  .uuid()
  .describe('Global unique identifier of the object (UUID)')

/**
 * @summary Reusable schema for CHANGEID field representing the identifier of the changing transaction.
 *
 * @description This schema should be used in all GAR item schemas that require a CHANGEID field.
 * The `describe()` method is called here to provide a common description.
 * Use `.optional()` only when applying this schema in specific contexts where the field is optional.
 *
 * @example
 * ```ts
 * const parsedId = ChangeId.parse('12345');
 * ```
 */
export const ChangeId = z.coerce
  .number<string>()
  .int()
  .describe('Identifier of the changing transaction')

/**
 * @summary Reusable schema for OPERTYPEID field representing the operation type identifier.
 *
 * @description This schema should be used in all GAR item schemas that require an OPERTYPEID field.
 * The `describe()` method is called here to provide a common description.
 * Use `.optional()` only when applying this schema in specific contexts where the field is optional.
 *
 * @example
 * ```ts
 * const parsedId = OperationTypeId.parse('12345');
 * ```
 */
export const OperationTypeId = z.coerce
  .number<string>()
  .int()
  .describe('Operation type identifier')

/**
 * @summary Reusable schema for TYPEID field representing the parameter type identifier.
 *
 * @description This schema should be used in all GAR item schemas that require a TYPEID field.
 * The `describe()` method is called here to provide a common description.
 * Use `.optional()` only when applying this schema in specific contexts where the field is optional.
 *
 * @example
 * ```ts
 * const parsedId = ParamTypeId.parse('12345');
 * ```
 */
export const ParamTypeId = z.coerce
  .number<string>()
  .int()
  .describe('Parameter type identifier')

/**
 * @summary Reusable schema for PREVID field representing the identifier of the record linking to the previous historical record.
 *
 * @description This schema should be used in all GAR item schemas that require a PREVID field.
 * The `describe()` method is called here to provide a common description.
 * Use `.optional()` only when applying this schema in specific contexts where the field is optional.
 *
 * @example
 * ```ts
 * const parsedId = PrevId.parse('12345');
 * ```
 */
export const PrevId = z.coerce
  .number<string>()
  .int()
  .describe('Identifier of the record linking to the previous historical record')

/**
 * @summary Reusable schema for NEXTID field representing the identifier of the record linking to the subsequent historical record.
 *
 * @description This schema should be used in all GAR item schemas that require a NEXTID field.
 * The `describe()` method is called here to provide a common description.
 * Use `.optional()` only when applying this schema in specific contexts where the field is optional.
 *
 * @example
 * ```ts
 * const parsedId = NextId.parse('12345');
 * ```
 */
export const NextId = z.coerce
  .number<string>()
  .int()
  .describe('Identifier of the record linking to the subsequent historical record')

/**
 * @summary Reusable schema for ID field representing the unique identifier of the record (primary key).
 *
 * @description This schema should be used in all GAR item schemas that require an ID field as primary key.
 * The `describe()` method is called here to provide a common description.
 * This field should NOT be optional as it represents the primary key.
 *
 * @example
 * ```ts
 * const parsedId = Id.parse('12345');
 * ```
 */
export const Id = z.coerce
  .number<string>()
  .int()
  .describe('Unique identifier of the record. Key field')
